import ApiService from './api';
import { safeArray } from '../utils/money';
import {
  OnboardingApplication,
  OnboardingForm,
  OnboardingResult,
  OnboardingStatus,
  CustomOnboardingRequest,
  CustomOnboardingResponse,
  OnboardingProgress,
} from '@/types';

// 提交入驻申请的请求体 - 对齐后端 API
export interface SubmitOnboardingRequest {
  plugin_name?: string;              // stripe_connect, samsum 等
  email: string;
  country: string;
  business_type: string;             // individual, company, non_profit
  // Stripe Connect 可选字段
  first_name?: string;
  last_name?: string;
  phone?: string;
  dob?: {
    day: number;
    month: number;
    year: number;
  };
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  // 公司类型额外字段
  company_name?: string;
  company_tax_id?: string;
  company_phone?: string;
  company_address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  // 业务资料
  mcc?: string;
  url?: string;
  business_description?: string;
  support_phone?: string;
  support_email?: string;
}

// Stripe Connect 白标补验证 - 缺失项
export interface ConnectVerificationMissing {
  key: string;
  label: string;                     // 已是中文，如「银行账户」「出生日」「身份证件」
}

// Stripe Connect 白标补验证 - 环节状态
export interface ConnectVerificationStep {
  name: string;
  status: string;                    // verified / pending / failed 等
  message: string;
}

// Stripe Connect 白标补验证状态
export interface ConnectVerification {
  configured: boolean;               // 是否存在 Connect 申请
  application_id: string;
  status: 'approved' | 'pending' | 'rejected';
  ready: boolean;                    // true 表示已通过，可正常收款
  missing: ConnectVerificationMissing[];
  steps: ConnectVerificationStep[];
}

// 入驻要求 - 对应后端 GetOnboardingRequirements 返回
export interface OnboardingRequirements {
  required_fields?: string[];
  required_documents?: string[];
  additional_documents?: Record<string, string[]>;
  verification_steps?: Array<{
    name: string;
    description: string;
  }>;
}

export class OnboardingService {
  /**
   * 获取入驻要求（必填字段/证件/验证环节）
   * GET /api/v1/merchant/onboarding/requirements
   */
  static async getOnboardingRequirements(plugin?: string): Promise<OnboardingRequirements> {
    const response = await ApiService.get<OnboardingRequirements>(
      '/merchant/onboarding/requirements',
      plugin ? { plugin } : undefined
    );
    return response.data;
  }

  /**
   * 获取入驻表单配置
   * GET /api/v1/merchant/onboarding/form
   */
  static async getOnboardingForm(): Promise<OnboardingForm> {
    const response = await ApiService.get<{ data: OnboardingForm }>('/merchant/onboarding/form');
    return response.data.data;
  }

  /**
   * 提交入驻申请
   * POST /api/v1/merchant/onboarding/submit
   */
  static async submitApplication(data: SubmitOnboardingRequest): Promise<OnboardingResult> {
    const response = await ApiService.post<{ data: OnboardingResult }>(
      '/merchant/onboarding/submit',
      data
    );
    return response.data.data;
  }

  /**
   * 获取入驻申请列表
   * GET /api/v1/merchant/onboarding/applications
   */
  static async listApplications(
    page: number = 1,
    limit: number = 20
  ): Promise<{
    data: OnboardingApplication[];
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  }> {
    const response = await ApiService.get('/merchant/onboarding/applications', { page, limit });
    return {
      ...response.data,
      data: safeArray<OnboardingApplication>(response.data?.data),
    };
  }

  /**
   * 检查入驻状态
   * GET /api/v1/merchant/onboarding/applications/:id
   */
  static async checkStatus(applicationId: string): Promise<{
    application_id: string;
    status: string;
    submitted_at: string;
    updated_at: string;
    missing_fields?: string[];
    missing_documents?: string[];
    verification_steps?: Array<{
      name: string;
      status: string;
      message: string;
    }>;
  }> {
    const response = await ApiService.get(`/merchant/onboarding/applications/${applicationId}`);
    return response.data.data;
  }

  /**
   * 获取入驻失败详情
   * GET /api/v1/merchant/onboarding/applications/:id/failure
   */
  static async getFailureDetails(applicationId: string): Promise<{
    application_id: string;
    reasons: string[];
    field_errors?: Record<string, string>;
    document_errors?: Record<string, string>;
    suggested_fixes?: Record<string, string>;
  }> {
    const response = await ApiService.get(`/merchant/onboarding/applications/${applicationId}/failure`);
    return response.data.data;
  }

  /**
   * 重新提交入驻申请
   * PUT /api/v1/merchant/onboarding/applications/:id
   */
  static async resubmitApplication(
    applicationId: string,
    data: Partial<SubmitOnboardingRequest>
  ): Promise<OnboardingResult> {
    const response = await ApiService.put<{ data: OnboardingResult }>(
      `/merchant/onboarding/applications/${applicationId}`,
      data
    );
    return response.data.data;
  }

  /**
   * 获取可用的入驻插件列表
   * GET /api/v1/merchant/onboarding/plugins
   */
  static async getAvailablePlugins(): Promise<Array<{
    provider_type: string;
    provider_name: string;
    display_name: string;
    description: string;
    version: string;
    required_groups: string[];
    supported_countries: string[];
    supported_business_types: string[];
  }>> {
    const response = await ApiService.get('/merchant/onboarding/plugins');
    return safeArray(response.data?.data);
  }

  // ============ Stripe Connect 白标补验证 ============

  /**
   * 获取 Stripe Connect 账户补验证状态（白标，不跳转）
   * GET /api/v1/merchant/onboarding/connect/verification
   */
  static async getConnectVerification(): Promise<ConnectVerification> {
    const response = await ApiService.get<{ data: ConnectVerification }>(
      '/merchant/onboarding/connect/verification'
    );
    return response.data.data;
  }

  /**
   * 白标提交补充资料（经 Stripe Accounts API，不跳转托管页）
   * PUT /api/v1/merchant/onboarding/applications/:id
   */
  static async resubmitConnectApplication(
    applicationId: string,
    data: Record<string, any>
  ): Promise<{ status: string; next_steps?: string[]; [key: string]: any }> {
    const response = await ApiService.put<{ data: any }>(
      `/merchant/onboarding/applications/${applicationId}`,
      data
    );
    return response.data.data;
  }

  /**
   * 上传入驻证件文档（经 Stripe Files API，multipart，不跳转）
   * POST /api/v1/merchant/onboarding/documents
   * form 需包含 file 及 document_type / purpose 字段
   */
  static async uploadOnboardingDocument(
    form: FormData
  ): Promise<{ id?: string; document_type?: string; [key: string]: any }> {
    const response = await ApiService.post<{ data: any }>(
      '/merchant/onboarding/documents',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  }

  // ============ 自定义入驻 (完整数据收集，无跳转) ============

  /**
   * 获取自定义入驻表单配置
   * GET /api/v1/merchant/onboarding/custom/form
   */
  static async getCustomOnboardingForm(
    country: string = 'US',
    businessType: string = 'individual'
  ): Promise<OnboardingForm> {
    const response = await ApiService.get<{ data: OnboardingForm }>(
      '/merchant/onboarding/custom/form',
      { country, business_type: businessType }
    );
    return response.data.data;
  }

  /**
   * 提交自定义入驻申请（完整数据，无跳转）
   * POST /api/v1/merchant/onboarding/custom/submit
   */
  static async submitCustomOnboarding(
    data: CustomOnboardingRequest
  ): Promise<CustomOnboardingResponse> {
    const response = await ApiService.post<{ data: CustomOnboardingResponse }>(
      '/merchant/onboarding/custom/submit',
      data
    );
    return response.data.data;
  }

  /**
   * 保存入驻步骤数据（分步保存）
   * POST /api/v1/merchant/onboarding/custom/step
   */
  static async saveOnboardingStep(
    applicationId: string,
    step: number,
    data: Record<string, any>
  ): Promise<OnboardingProgress> {
    const response = await ApiService.post<{ data: OnboardingProgress }>(
      '/merchant/onboarding/custom/step',
      { application_id: applicationId, step, data }
    );
    return response.data.data;
  }

  /**
   * 获取入驻进度
   * GET /api/v1/merchant/onboarding/custom/progress/:id
   */
  static async getOnboardingProgress(applicationId: string): Promise<OnboardingProgress> {
    const response = await ApiService.get<{ data: OnboardingProgress }>(
      `/merchant/onboarding/custom/progress/${applicationId}`
    );
    return response.data.data;
  }
}

export default OnboardingService;
